const A1_LANDSCAPE_DIMENSIONS_CM = [84.1, 59.4] as const
const A2_LANDSCAPE_DIMENSIONS_CM = [59.4, 42] as const
const A3_LANDSCAPE_DIMENSIONS_CM = [42, 29.7] as const

/** PDF page margins in cm (relative to paper dimensions). */
const PLAN_PAGE_MARGIN_LEFT_CM = 2
const PLAN_PAGE_MARGIN_RIGHT_CM = 1
const PLAN_PAGE_MARGIN_TOP_CM = 1
const PLAN_PAGE_MARGIN_BOTTOM_CM = 1

type PaperFormat = 'A1' | 'A2' | 'A3'

interface ScaledPaperLimits {
  xlim: [number, number]
  ylim: [number, number]
}

const paperDimensionsLandscapeCm = (paperFormat: PaperFormat): [number, number] => {
  switch (paperFormat) {
    case 'A1':
      return [...A1_LANDSCAPE_DIMENSIONS_CM]
    case 'A2':
      return [...A2_LANDSCAPE_DIMENSIONS_CM]
    case 'A3':
      return [...A3_LANDSCAPE_DIMENSIONS_CM]
    default:
      return [...A2_LANDSCAPE_DIMENSIONS_CM]
  }
}

const getScaledPaperLimits = (planSettings: PdfPlanSettings): ScaledPaperLimits => {
  const paperFormat = planSettings.paperFormat
  const orientation = planSettings.orientation === 'portrait' ? 'portrait' : 'landscape'
  const scaleDenominator = planSettings.scale
  const userDefinedOrigin = planSettings.user_defined_origin

  const [landscapeWidthCm, landscapeHeightCm] = paperDimensionsLandscapeCm(paperFormat)
  const [formatWidthCm, formatHeightCm] =
    orientation === 'landscape'
      ? [landscapeWidthCm, landscapeHeightCm]
      : [landscapeHeightCm, landscapeWidthCm]

  const formatWidthM = formatWidthCm / 100
  const formatHeightM = formatHeightCm / 100

  const limitsLowerBound = {
    x: userDefinedOrigin.x * scaleDenominator * -1,
    y: userDefinedOrigin.y * scaleDenominator * -1,
  }
  const limitsUpperBound = {
    x: (formatWidthM - userDefinedOrigin.x) * scaleDenominator,
    y: (formatHeightM - userDefinedOrigin.y) * scaleDenominator,
  }

  return {
    xlim: [limitsLowerBound.x, limitsUpperBound.x],
    ylim: [limitsLowerBound.y, limitsUpperBound.y],
  }
}

const getScaledPaperMarginLimits = (planSettings: PdfPlanSettings): ScaledPaperLimits => {
  const { xlim, ylim } = getScaledPaperLimits(planSettings)
  const scaleDenominator = planSettings.scale

  const marginLeft = (PLAN_PAGE_MARGIN_LEFT_CM / 100) * scaleDenominator
  const marginRight = (PLAN_PAGE_MARGIN_RIGHT_CM / 100) * scaleDenominator
  const marginTop = (PLAN_PAGE_MARGIN_TOP_CM / 100) * scaleDenominator
  const marginBottom = (PLAN_PAGE_MARGIN_BOTTOM_CM / 100) * scaleDenominator

  return {
    xlim: [xlim[0] + marginLeft, xlim[1] - marginRight],
    ylim: [ylim[0] + marginBottom, ylim[1] - marginTop],
  }
}

export type { ScaledPaperLimits }
export {
  PLAN_PAGE_MARGIN_BOTTOM_CM,
  PLAN_PAGE_MARGIN_LEFT_CM,
  PLAN_PAGE_MARGIN_RIGHT_CM,
  PLAN_PAGE_MARGIN_TOP_CM,
  getScaledPaperMarginLimits,
}
export default getScaledPaperLimits
